import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { Router } from '@angular/router';
import { TenantsService, Tenant } from '../tenants.service';
import { TenantFormDialogComponent } from '../tenant-form-dialog/tenant-form-dialog.component';
import { AuthService } from '../../../../core/services/auth.service';
import { ConfirmationService } from '../../../../shared/services/confirmation.service';
import { HasPermissionDirective } from '../../../../core/directives/has-permission.directive';
import { PageHeaderService } from '../../../../shared/services/page-header.service';
import { SharedModule } from '../../../../shared/shared.module';

@Component({
  selector: 'app-tenants-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDialogModule,
    MatMenuModule,
    MatDividerModule,
    HasPermissionDirective,
    SharedModule,
  ],
  templateUrl: './tenants-list.component.html',
  styleUrls: ['./tenants-list.component.scss'],
})
export class TenantsListComponent implements OnInit, OnDestroy {
  tenants: Tenant[] = [];
  loading = false;
  displayedColumns: string[] = [
    'name',
    'business_type',
    'country',
    'resources',
    'status',
    'created_at',
    'actions',
  ];

  constructor(
    private tenantsService: TenantsService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router,
    private authService: AuthService,
    private confirmationService: ConfirmationService,
    private pageHeader: PageHeaderService,
  ) {}

  ngOnDestroy(): void {
    this.pageHeader.clear();
  }

  private configurePageHeader(): void {
    this.pageHeader.set({
      title: 'Tenant Management',
      icon: 'business',
      backRoute: '/dashboard',
      actions: [
        {
          label: 'Refresh',
          icon: 'refresh',
          variant: 'stroked',
          permission: 'tenant.read',
          disabled: () => this.loading,
          handler: () => this.loadTenants(),
        },
        {
          label: 'Add Tenant',
          icon: 'add_business',
          variant: 'primary',
          permission: 'tenant.create',
          handler: () => this.onCreateTenant(),
        },
      ],
    });
  }

  ngOnInit(): void {
    // Redirect Admin users to dashboard - they shouldn't access tenant management
    const currentUser = this.authService.getCurrentUserValue();
    if (currentUser?.role?.slug === 'admin') {
      this.snackBar.open(
        'Access denied: Tenant management is restricted to Super Admins',
        'Close',
        {
          duration: 3000,
        },
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    this.configurePageHeader();
    this.loadTenants();
  }

  loadTenants(): void {
    this.loading = true;
    this.tenantsService.getTenants().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.tenants = response.data;
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading tenants:', error);
        this.snackBar.open('Failed to load tenants', 'Close', {
          duration: 3000,
        });
        this.loading = false;
      },
    });
  }

  onCreateTenant(): void {
    const dialogRef = this.dialog.open(TenantFormDialogComponent, {
      width: '660px',
      maxHeight: '90vh',
      data: {},
    });
    dialogRef.afterClosed().subscribe((created) => {
      if (created) this.loadTenants();
    });
  }

  onViewTenant(tenant: Tenant): void {
    this.router.navigate(['/admin/tenants', tenant.id]);
  }

  onEditTenant(tenant: Tenant): void {
    const dialogRef = this.dialog.open(TenantFormDialogComponent, {
      width: '660px',
      maxHeight: '90vh',
      data: { tenantId: tenant.id },
    });
    dialogRef.afterClosed().subscribe((updated) => {
      if (updated) this.loadTenants();
    });
  }

  onToggleActive(tenant: Tenant): void {
    this.tenantsService.toggleActive(tenant.id).subscribe({
      next: (response) => {
        if (response.success) {
          tenant.is_active = !tenant.is_active;
          this.snackBar.open(
            `Tenant ${tenant.is_active ? 'activated' : 'deactivated'} successfully`,
            'Close',
            { duration: 3000 },
          );
        }
      },
      error: (error) => {
        console.error('Error toggling tenant status:', error);
        this.snackBar.open('Failed to update tenant status', 'Close', {
          duration: 3000,
        });
      },
    });
  }

  onDeleteTenant(tenant: Tenant): void {
    this.confirmationService.confirm({
      title: 'Delete tenant',
      message: `Are you sure you want to delete "${tenant.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      type: 'danger',
    }).subscribe(confirmed => {
      if (!confirmed) return;
      this.tenantsService.deleteTenant(tenant.id).subscribe({
        next: (response) => {
          if (response.success) {
            this.snackBar.open('Tenant deleted successfully', 'Close', {
              duration: 3000,
            });
            this.loadTenants();
          }
        },
        error: (error) => {
          console.error('Error deleting tenant:', error);
          const message = error.error?.message || 'Failed to delete tenant';
          this.snackBar.open(message, 'Close', { duration: 5000 });
        },
      });
    });
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
}
